<template>
    <Modal>
        <TypeSelectScreen
            v-if="step === STEPS.TYPE_SELECT"
            :on-select="onActionSelect"
        />
        <CreateShowScreen
            v-if="step === STEPS.CREATE_SHOW"
            :on-submit="onShowSubmit"
        />
        <ImportShowScreen
            v-if="step === STEPS.IMPORT_SHOW"
            :on-submit="onShowSubmit"
        />
        <ImportShowConfirmScreen
            v-if="step === STEPS.CONFIRM_IMPORT"
            :on-continue="() => nextStep()"
        />
        <PlanSelectScreen
            v-if="step === STEPS.PLAN_SELECT"
            :on-select="onPlanSelect"
        />
        <TrialScreen
            v-if="step === STEPS.TRIAL"
            :on-continue="onTrialSelect"
        />
        <CheckoutScreen
            v-if="step === STEPS.CHECKOUT"
            :on-subscribe="onSubscribe"
        />
        <template v-slot:footer>
            <ModalFooter />
        </template>
    </Modal>
</template>
<script>

/**
 * External dependencies.
 */
import { mapState, mapActions, mapMutations } from 'vuex';

/**
 * Internal dependencies.
 */
import apiCall from '@/utils/apiCall';
import CheckoutScreen from './CheckoutScreen';
import CreateShowScreen from './CreateShowScreen';
import ImportShowConfirmScreen from './ImportShowConfirmScreen';
import ImportShowScreen from './ImportShowScreen';
import Modal from './Modal';
import ModalFooter from './ModalFooter';
import PlanSelectScreen from './PlanSelectScreen';
import TrialScreen from './TrialScreen';
import TypeSelectScreen from './TypeSelectScreen';
import { ACTION, STEPS } from './config';

export default {
    components: {
        CheckoutScreen,
        CreateShowScreen,
        ImportShowConfirmScreen,
        ImportShowScreen,
        Modal,
        ModalFooter,
        PlanSelectScreen,
        TrialScreen,
        TypeSelectScreen,
    },

    computed: {
        ...mapState([
            'currentSubscription',
            'billingPlans',
        ]),

        ...mapState('show/modal', [
            'step',
            'action',
            'show',
            'plan',
        ]),

        /**
         * Gets the show actions.
         *
         * @returns {object} Show actions.
         */
        ACTION() {
            return ACTION;
        },

        /**
         * Gets the steps.
         *
         * @returns {object} Steps.
         */
        STEPS() {
            return STEPS;
        },
    },

    /**
     * Handles the component creation.
     */
    async created() {
        await this.preloadData();
        await this.prefetchCouponDiscounts();
    },

    methods: {
        ...mapActions([
            'convertShow',
            'getBillingPlans',
            'loadPlanFeatures',
            'loadShowCategories',
            'loadUserSubscription',
            'updateUser',
        ]),

        ...mapActions('show/modal', [
            'nextStep',
            'previousStep',
            'closeModal',
        ]),

        ...mapMutations('register', {
            setRegisterFreshState: 'setFreshState',
        }),

        ...mapMutations('show/modal', [
            'setAction',
            'setShow',
            'setPlan',
            'setTrialEnabled',
            'setPromo',
            'setTrialSkippedByPromo',
            'setCouponDiscount',
            'setFreshState',
        ]),

        /**
         * Preloads data.
         *
         * @returns {Promise} Promises.
         */
        preloadData() {
            return Promise.all([
                this.loadUserSubscription(),
                this.getBillingPlans(),
                this.loadPlanFeatures(),
                this.loadShowCategories(),
            ]);
        },

        /**
         * Prefetches inbound-coupon discounts for each paid plan.
         *
         * Resolves the `fusebox_promo` cookie against every paid plan so the
         * plan chooser can show discounted prices before a plan is selected.
         * Only runs for new subscribers with a promo cookie present.
         *
         * @returns {Promise} Promise.
         */
        async prefetchCouponDiscounts() {
            const code = this.$cookies.get('fusebox_promo');

            if (! code || this.currentSubscription || ! this.billingPlans) {
                return;
            }

            await Promise.all(
                this.billingPlans
                    .filter((plan) => ! plan.isFree)
                    .map(async (plan) => {
                        const { status, data } = await apiCall(
                            this,
                            'post',
                            `${this.$appUrl}/api/billing/promo/${code}`,
                            { plan: plan.slug }
                        );

                        if ([200, 201].includes(status) && data.coupon) {
                            this.setCouponDiscount({ plan: plan.slug, coupon: data.coupon });
                        }
                    })
            );
        },

        /**
         * Handles action selection.
         *
         * @param  {string} action Show action.
         */
        onActionSelect(action) {
            this.setShow(undefined);
            this.setAction(action);
            this.nextStep();
        },

        /**
         * Handles show submit.
         *
         * @param  {object} show Show object.
         */
        onShowSubmit(show) {
            this.setShow(show);
            this.nextStep();
        },

        /**
         * Handles plan select.
         *
         * New subscribers arriving with a valid inbound coupon promo are sent
         * straight to a discounted checkout with no trial; everyone else keeps
         * the regular trial flow.
         *
         * @param  {object} plan Selected plan.
         * @returns {Promise} Promise.
         */
        async onPlanSelect(plan) {
            this.setPlan(plan);

            const couponPromo = this.currentSubscription
                ? null
                : await this.resolveInboundCouponPromo(plan);

            if (couponPromo) {
                this.setPromo(couponPromo);
                this.setTrialEnabled(false);
                this.setTrialSkippedByPromo(true);
            } else {
                this.setPromo(null);
                this.setTrialSkippedByPromo(false);
            }

            this.nextStep();
        },

        /**
         * Resolves an inbound coupon promo from the promo cookie, if any.
         *
         * Returns the promo code only when a `fusebox_promo` cookie is present
         * and resolves to a non-expired `coupon`-type promo applicable to the
         * selected plan. Returns null otherwise (no cookie, invalid/expired,
         * not applicable, or a non-coupon promo type).
         *
         * @param  {object} plan Selected plan.
         * @returns {Promise<?string>} Promo code, or null.
         */
        async resolveInboundCouponPromo(plan) {
            const code = this.$cookies.get('fusebox_promo');

            if (! code) {
                return null;
            }

            const { status, data } = await apiCall(
                this,
                'post',
                `${this.$appUrl}/api/billing/promo/${code}`,
                { plan: plan.slug }
            );

            if (! [200, 201].includes(status)) {
                return null;
            }

            return 'coupon' === data.type ? code : null;
        },

        /**
         * Handle trial select.
         *
         * @param  {boolean} trialEnabled Indicates whether trial was selected or not.
         */
        onTrialSelect({ trial }) {
            this.setTrialEnabled(trial);
            this.setPromo(! trial ? 'trial-trade-in' : null);
            this.nextStep();
        },

        /**
         * Handles subscribe action.
         *
         * @param  {object} subscription Subscription object.
         */
        async onSubscribe(subscription) {
            try {
                await this.convertShow({
                    subscriptionId: subscription.id,
                    showHash: this.show.hash,
                    planId: this.plan.id,
                });
            } catch (e) {
                //
                return;
            }

            if (this.$route.path !== `/shows/${this.show.hash}`) {
                this.$router.push(`/shows/${this.show.hash}`);
            }

            this.closeModal();
            this.setFreshState();
            this.setRegisterFreshState();

            await Promise.all([
                this.updateUser(),
                this.loadUserSubscription({ force: true }),
            ]);
        },
    },
};
</script>
<style scoped lang="scss">
    @use './ShowModal.scss';
</style>
